import { useState } from 'react';
import { Control, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type { ProviderEditorFormData } from './schemas';
import { SpecialtyCoveragePrototype } from './prototype/SpecialtyCoveragePrototype';
import { UnsavedChangesBanner } from './UnsavedChangesBanner';
import { AccountManagementSection } from './sections/AccountManagementSection';
import { BasicInformationSection } from './sections/BasicInformationSection';
import { ContactDetailsSection } from './sections/ContactDetailsSection';
import { ProfileMediaSection } from './sections/ProfileMediaSection';
import { ProfessionalCredentialsSection } from './sections/ProfessionalCredentialsSection';
import { ProfessionalIdentitySection } from './sections/ProfessionalIdentitySection';
import { ServicesOfferingsSection } from './sections/ServicesOfferingsSection';
import { ServicesCatalogSection } from './sections/ServicesCatalogSection';
import { AvailabilitySection } from './sections/AvailabilitySection';
import { SpecialtiesCoverageSection } from './sections/SpecialtiesCoverageSection';
import { TaxonomiesSection } from './sections/TaxonomiesSection';
import { PaymentOptionsSection } from './sections/PaymentOptionsSection';
import { AdditionalDetailsSection } from './sections/AdditionalDetailsSection';
import { ProviderDocumentsManager } from '@/features/provider-directory/components/ProviderDocumentsManager';
import { VettingCheckList } from '@/features/vetting/components/VettingCheckList';
import { useAdminAuth } from '../../hooks/useAdminAuth';

type InformationTabProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
  userId?: string;
  kindeId?: string;
  /** Embedded intake form URL for read-only display (Feature 103 pilot). */
  intakeFormUrl?: string | null;
  providerId?: string;
  isDirty?: boolean;
  setValue: UseFormSetValue<ProviderEditorFormData>;
  serverFieldErrors?: string[];
};

/**
 * Collapsible Section Component
 */
function CollapsibleSection({
  title,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-gray-50"
      >
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {isOpen && <div className="border-t border-gray-200 px-6 py-6">{children}</div>}
    </div>
  );
}

/**
 * InformationTab - Main form tab with 12 collapsible sections
 * 
 * Phase 4 Implementation:
 * - Complete 12-section implementation with dedicated section components
 * - Account Management (with read-only user_id and kinde_id)
 * - Basic Information
 * - Contact Details
 * - Profile Media (with inline uploads and gallery management)
 * - Professional Credentials
 * - Professional Identity
 * - Services & Offerings
 * - Availability (placeholder)
 * - Geographic Information (placeholder)
 * - Taxonomies (placeholder)
 * - Payment Options
 * - Additional Details
 */
export function InformationTab({
  control,
  errors,
  userId,
  kindeId,
  intakeFormUrl,
  providerId,
  isDirty = false,
  setValue,
  serverFieldErrors = [],
}: InformationTabProps) {
  const [searchParams] = useSearchParams();
  const showCoveragePrototype =
    import.meta.env.DEV && searchParams.has('coveragePrototype');

  // Track which sections are open
  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(['account-management', 'basic-information'])
  );

  // Feature 099: closest existing permission tier to the not-yet-built OVERRIDE_VETTING_CHECK
  // (design.md D6/O2 — client hasn't confirmed the senior-admin mapping yet).
  const { canOverrideVettingCheck, canManageUsersAndResources } = useAdminAuth();

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <div role="tabpanel" className="space-y-4">
      {/* Unsaved Changes Banner */}
      {isDirty && <UnsavedChangesBanner />}

      {/* Section 1: Account Management */}
      <CollapsibleSection
        title="Account Management"
        isOpen={openSections.has('account-management')}
        onToggle={() => toggleSection('account-management')}
      >
        <AccountManagementSection
          control={control}
          errors={errors}
          userId={userId}
          kindeId={kindeId}
        />
      </CollapsibleSection>

      {/* Section 2: Basic Information */}
      <CollapsibleSection
        title="Basic Information"
        isOpen={openSections.has('basic-information')}
        onToggle={() => toggleSection('basic-information')}
      >
        <BasicInformationSection control={control} errors={errors} setValue={setValue} />
      </CollapsibleSection>

      {/* Section 3: Contact Details */}
      <CollapsibleSection
        title="Contact Details"
        isOpen={openSections.has('contact-details')}
        onToggle={() => toggleSection('contact-details')}
      >
        <ContactDetailsSection control={control} errors={errors} intakeFormUrl={intakeFormUrl} />
      </CollapsibleSection>

      {/* Section 4: Profile Media */}
      <CollapsibleSection
        title="Profile Media"
        isOpen={openSections.has('profile-media')}
        onToggle={() => toggleSection('profile-media')}
      >
        <ProfileMediaSection control={control} errors={errors} />
      </CollapsibleSection>

      {/* Feature 099: Vetting Checks (Checkr background check, UI preview — only available in edit mode) */}
      {providerId ? (
        <CollapsibleSection
          title="Vetting Checks"
          isOpen={openSections.has('vetting-checks')}
          onToggle={() => toggleSection('vetting-checks')}
        >
          <VettingCheckList
            providerId={providerId}
            canOverride={canOverrideVettingCheck}
            canManage={canManageUsersAndResources}
            // Send/Restart use the SAVED email/name/address — block them until edits are saved
            hasUnsavedChanges={isDirty}
          />
        </CollapsibleSection>
      ) : null}

      {/* Feature 073: PDF Documents (only available in edit mode where providerId is known) */}
      {providerId ? (
        <CollapsibleSection
          title="Documents (PDFs)"
          isOpen={openSections.has('documents')}
          onToggle={() => toggleSection('documents')}
        >
          <ProviderDocumentsManager mode="admin" providerId={providerId} />
        </CollapsibleSection>
      ) : null}

      {/* Section 5: Professional Credentials */}
      <CollapsibleSection
        title="Professional Credentials"
        isOpen={openSections.has('professional-credentials')}
        onToggle={() => toggleSection('professional-credentials')}
      >
        <ProfessionalCredentialsSection control={control} errors={errors} />
      </CollapsibleSection>

      {/* Section 6: Professional Identity */}
      <CollapsibleSection
        title="Professional Identity"
        isOpen={openSections.has('professional-identity')}
        onToggle={() => toggleSection('professional-identity')}
      >
        <ProfessionalIdentitySection control={control} errors={errors} />
      </CollapsibleSection>

      {/* Section 7: Services & Offerings */}
      <CollapsibleSection
        title="Services & Offerings"
        isOpen={openSections.has('services-offerings')}
        onToggle={() => toggleSection('services-offerings')}
      >
        <ServicesOfferingsSection control={control} errors={errors} />
      </CollapsibleSection>

      {/* Section 7b: Services Catalog */}
      <CollapsibleSection
        title="Services Catalog"
        isOpen={openSections.has('services-catalog')}
        onToggle={() => toggleSection('services-catalog')}
      >
        <ServicesCatalogSection control={control} />
      </CollapsibleSection>

      {/* Section 8: Availability (placeholder) */}
      <CollapsibleSection
        title="Availability"
        isOpen={openSections.has('availability')}
        onToggle={() => toggleSection('availability')}
      >
        <AvailabilitySection control={control} errors={errors} />
      </CollapsibleSection>

      {showCoveragePrototype ? (
        <SpecialtyCoveragePrototype />
      ) : (
        <>
          {/* Per-specialty coverage replaces provider-level Geographic Coverage writes */}
          <CollapsibleSection
            title="Specialties & Coverage"
            isOpen={openSections.has('specialties-coverage')}
            onToggle={() => toggleSection('specialties-coverage')}
          >
            <SpecialtiesCoverageSection
              control={control}
              errors={errors}
              serverFieldErrors={serverFieldErrors}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Areas of Expertise"
            isOpen={openSections.has('taxonomies')}
            onToggle={() => toggleSection('taxonomies')}
          >
            <TaxonomiesSection control={control} errors={errors} />
          </CollapsibleSection>
        </>
      )}

      {/* Section 11: Payment Options */}
      <CollapsibleSection
        title="Payment Options"
        isOpen={openSections.has('payment-options')}
        onToggle={() => toggleSection('payment-options')}
      >
        <PaymentOptionsSection control={control} errors={errors} />
      </CollapsibleSection>

      {/* Section 12: Additional Details */}
      <CollapsibleSection
        title="Additional Details"
        isOpen={openSections.has('additional-details')}
        onToggle={() => toggleSection('additional-details')}
      >
        <AdditionalDetailsSection control={control} errors={errors} />
      </CollapsibleSection>
    </div>
  );
}
